// Upload helpers — self-hosted filesystem storage via `POST /api/uploads`.
// A resolved promise here always means the backend confirmed a stored URL.
import { deleteAsset, uploadAsset, USE_MOCK_API, type UploadedAsset } from '@/lib/api/http';

export type UploadFolder =
  | 'products'
  | 'product-colors'
  | 'categories'
  | 'subcategories'
  | 'hero'
  | 'size-guides'
  | 'sounds'
  | 'music'
  | 'banners';

/** Maps the legacy `bucket` + `prefix` call style onto a server folder. */
export function resolveFolder(bucket: string, prefix = ''): UploadFolder {
  const p = prefix.toLowerCase();
  if (bucket === 'sounds') return p.startsWith('tracks') ? 'music' : 'sounds';
  if (bucket === 'product-images') {
    if (p.startsWith('colors')) return 'product-colors';
    if (p.startsWith('sizeguides')) return 'size-guides';
    return 'products';
  }
  if (bucket === 'category-images') return p.startsWith('sub') ? 'subcategories' : 'categories';
  if (bucket === 'site-assets') return p.startsWith('hero') ? 'hero' : 'banners';
  return 'banners';
}

/** Mock-mode fallback: inline the file so the preview still renders something. */
function toDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.readAsDataURL(blob);
  });
}

export async function upload(
  folder: UploadFolder,
  file: Blob,
  filename?: string,
  replace?: string,
): Promise<UploadedAsset> {
  if (USE_MOCK_API) {
    const url = await toDataUrl(file);
    return { url, path: url, folder, filename: filename ?? 'upload' };
  }
  return uploadAsset(file, { folder, filename, replace });
}

export async function uploadImage(
  bucket: string,
  file: File,
  prefix = '',
  replace?: string,
): Promise<string> {
  const res = await upload(resolveFolder(bucket, prefix), file, file.name, replace);
  return res.url;
}

export async function uploadBlob(
  bucket: string,
  blob: Blob,
  ext = 'jpg',
  prefix = '',
  replace?: string,
): Promise<string> {
  const name = blob instanceof File && blob.name ? blob.name : `upload.${ext}`;
  const res = await upload(resolveFolder(bucket, prefix), blob, name, replace);
  return res.url;
}

/** Delete a stored file by its URL or path (idempotent, admin-only). */
export async function removeMedia(pathOrUrl: string): Promise<void> {
  if (!pathOrUrl || USE_MOCK_API || pathOrUrl.startsWith('data:')) return;
  await deleteAsset(pathOrUrl);
}
